/**
 * Date Range Selector Component
 * Provides preset and custom date range selection for statistics
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Calendar } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export type DateRangePreset = 'last7days' | 'last30days' | 'last90days' | 'custom';

export interface DateRange {
  from: Date;
  to: Date;
}

interface DateRangeSelectorProps {
  value: DateRangePreset;
  dateRange: DateRange;
  onChange: (preset: DateRangePreset, range: DateRange) => void;
  className?: string;
}

/**
 * DateRangeSelector Component
 * Dropdown with preset options and custom date picker
 */
export function DateRangeSelector({ value, dateRange, onChange, className }: DateRangeSelectorProps) {
  const [isCustomPickerOpen, setIsCustomPickerOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState<Date | undefined>(dateRange.from);
  const [customTo, setCustomTo] = useState<Date | undefined>(dateRange.to);
  const [pendingCustomOpen, setPendingCustomOpen] = useState(false);

  // Open custom picker after value changes to 'custom'
  useEffect(() => {
    if (pendingCustomOpen && value === 'custom') {
      setIsCustomPickerOpen(true);
      setPendingCustomOpen(false);
    }
  }, [value, pendingCustomOpen]);

  // Sync custom dates with dateRange prop
  useEffect(() => {
    setCustomFrom(dateRange.from);
    setCustomTo(dateRange.to);
  }, [dateRange.from, dateRange.to]);

  const handlePresetChange = (preset: string) => {
    const today = new Date();
    let from: Date;
    let to: Date = today;

    if (preset === 'custom') {
      // Update parent state to 'custom' first
      // Keep current date range while user selects new dates
      onChange('custom', dateRange);
      // Use pending flag so popover opens after value updates and trigger becomes visible
      setPendingCustomOpen(true);
      return;
    }

    switch (preset) {
      case 'last7days':
        from = new Date(today);
        from.setDate(today.getDate() - 7);
        break;
      case 'last30days':
        from = new Date(today);
        from.setDate(today.getDate() - 30);
        break;
      case 'last90days':
        from = new Date(today);
        from.setDate(today.getDate() - 90);
        break;
      default:
        from = new Date(today);
        from.setDate(today.getDate() - 30);
    }

    onChange(preset as DateRangePreset, { from, to });
  };

  const handleApplyCustomRange = () => {
    if (customFrom && customTo) {
      onChange('custom', { from: customFrom, to: customTo });
      setIsCustomPickerOpen(false);
    }
  };

  const getDisplayLabel = () => {
    if (value === 'custom') {
      return `${format(dateRange.from, 'MMM dd, yyyy')} - ${format(dateRange.to, 'MMM dd, yyyy')}`;
    }

    switch (value) {
      case 'last7days':
        return 'Last 7 Days';
      case 'last30days':
        return 'Last 30 Days';
      case 'last90days':
        return 'Last 90 Days';
      default:
        return 'Select Range';
    }
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Select value={value} onValueChange={handlePresetChange}>
        <SelectTrigger className="w-full md:w-[280px]">
          <SelectValue placeholder="Select date range">
            {getDisplayLabel()}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="last7days">Last 7 Days</SelectItem>
          <SelectItem value="last30days">Last 30 Days</SelectItem>
          <SelectItem value="last90days">Last 90 Days</SelectItem>
          <SelectItem value="custom">Custom Range</SelectItem>
        </SelectContent>
      </Select>

      {/* Custom Date Range Picker Modal */}
      <Popover open={isCustomPickerOpen} onOpenChange={setIsCustomPickerOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full md:w-[280px] justify-start text-left font-normal',
              value !== 'custom' && 'hidden'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {customFrom && customTo ? (
              `${format(customFrom, 'MMM dd, yyyy')} - ${format(customTo, 'MMM dd, yyyy')}`
            ) : (
              'Pick a date range'
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-4" align="end" side="bottom" sideOffset={4}>
          <div className="flex flex-col gap-4">
            {/* Calendars side by side on larger screens, stacked on mobile */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block text-center">From Date</label>
                <Calendar
                  mode="single"
                  selected={customFrom}
                  onSelect={setCustomFrom}
                  disabled={(date) => date > new Date() || (customTo ? date > customTo : false)}
                  initialFocus
                  className="rounded-md border"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block text-center">To Date</label>
                <Calendar
                  mode="single"
                  selected={customTo}
                  onSelect={setCustomTo}
                  disabled={(date) => date > new Date() || (customFrom ? date < customFrom : false)}
                  className="rounded-md border"
                />
              </div>
            </div>
            {/* Action buttons */}
            <div className="flex gap-2 justify-end border-t pt-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsCustomPickerOpen(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleApplyCustomRange}
                disabled={!customFrom || !customTo}
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
